import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ShareSlipModal, { buildYearlyShareData } from '../components/ShareSlipModal';
import './YearlySummary.css';

const thaiMonths = [
    '', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
    'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
];

function formatNumber(num) {
    if (num === null || num === undefined) return '0.00';
    return Number(num).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function YearlySummary() {
    const { yyyy } = useParams();
    const { user } = useAuth();
    const emailVerified = !!user?.email_verified;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [taxCertRequestMode, setTaxCertRequestMode] = useState(false); // default ปิด (พิมพ์ได้เลย)
    const [printing, setPrinting] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);

    useEffect(() => {
        fetchData();
    }, [yyyy]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [summaryRes, settingsRes] = await Promise.all([
                api.get(`/salary/yearly-summary/${yyyy}`),
                api.get('/request-mode').catch(() => ({ data: { tax_cert_request_mode: false } }))
            ]);
            setData(summaryRes.data);
            setTaxCertRequestMode(settingsRes.data.tax_cert_request_mode ?? false);
        } catch (err) {
            setError(err.response?.data?.error || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = async () => {
        if (!emailVerified) { window.location.href = '/profile#email'; return; }
        try {
            setPrinting(true);
            const response = await api.get(`/report/50-tawi/${yyyy}`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (err) {
            console.error('Print error:', err);
            alert('ไม่สามารถพิมพ์เอกสารได้');
        } finally {
            setPrinting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex-center" style={{ padding: '60px 0' }}>
                <div className="spinner"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-error">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                {error}
            </div>
        );
    }

    const { employee, year, summary, detailByType, monthlyDetail } = data;

    // แยกรายการรับและหัก (ตาม flag)
    const incomeItems = detailByType.filter(d => d.flag > 0);
    const deductionItems = detailByType.filter(d => d.flag < 0);

    // คำนวณยอดภาษี (รหัส 501) และ กบข. (รหัส 601, 602) รวมทั้งปี
    const totalTax = deductionItems
        .filter(item => item.code === '501')
        .reduce((sum, item) => sum + Math.abs(item.total_amt || 0), 0);

    const totalGpf = deductionItems
        .filter(item => item.code === '601' || item.code === '602')
        .reduce((sum, item) => sum + Math.abs(item.total_amt || 0), 0);

    // แปลงข้อมูลรายปีให้อยู่ในรูปแบบเดียวกับสลิปรายเดือน เพื่อใช้ ShareSlipModal ร่วมกัน
    const shareData = buildYearlyShareData(data);

    return (
        <div className="yearly-summary">
            {/* Breadcrumb */}
            <div className="breadcrumb">
                <Link to="/">หน้าหลัก</Link>
                <span>/</span>
                <span>สรุปรายปี {year + 543}</span>
            </div>

            {/* Header */}
            <div className="detail-header">
                <div className="header-info">
                    <h1>สรุปรายได้ประจำปี {year + 543}</h1>
                    <p className="employee-name">{employee.fullname}</p>
                </div>
                <div className="header-actions">
                    {/* แชร์สรุปรายปีเป็นรูปภาพ (ใช้ modal เดียวกับสลิปรายเดือน) */}
                    <button
                        className="share-slip-btn"
                        onClick={() => setShowShareModal(true)}
                        title="แชร์สรุปรายได้ประจำปีเป็นรูปภาพ"
                    >
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7"/>
                            <polyline points="16 6 12 2 8 6"/>
                            <line x1="12" y1="2" x2="12" y2="15"/>
                        </svg>
                        แชร์
                    </button>
                    {taxCertRequestMode ? (
                        <Link to="/request" className="steam-btn steam-btn-primary">
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 12h6M9 16h6M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2z"/>
                            </svg>
                            ขอหนังสือ 50 ทวิ
                        </Link>
                    ) : (
                        <button
                            className="steam-btn steam-btn-primary"
                            onClick={handlePrint}
                            disabled={printing || !emailVerified}
                            title={!emailVerified ? 'ต้องยืนยันอีเมลก่อนจึงจะพิมพ์ได้' : ''}
                        >
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/>
                            </svg>
                            {printing ? 'กำลังพิมพ์...' : (emailVerified ? 'พิมพ์หนังสือ 50 ทวิ' : 'ยืนยันอีเมลก่อนพิมพ์')}
                        </button>
                    )}
                </div>
            </div>

            {showShareModal && (
                <ShareSlipModal mode="yearly" data={shareData} yyyy={yyyy} mm={0} onClose={() => setShowShareModal(false)} />
            )}

            {/* Summary Cards */}
            <div className="summary-boxes">
                <div className="summary-box income">
                    <span className="summary-label">รายได้รวมทั้งปี</span>
                    <span className="summary-value">{formatNumber(summary.total_income)}</span>
                    <span className="summary-unit">บาท</span>
                </div>
                <div className="summary-box deduction">
                    <span className="summary-label">รายหักรวมทั้งปี</span>
                    <span className="summary-value">{formatNumber(summary.total_deduction)}</span>
                    <span className="summary-unit">บาท</span>
                </div>
                <div className="summary-box net">
                    <span className="summary-label">รับสุทธิรวม</span>
                    <span className="summary-value">{formatNumber((summary.total_income || 0) - (summary.total_deduction || 0))}</span>
                    <span className="summary-unit">บาท</span>
                </div>
            </div>

            {/* Tax & GPF Yearly Summary */}
            <div className="tax-gpf-yearly">
                <div className="tax-gpf-yearly-box tax">
                    <span className="tax-gpf-yearly-icon">
                        <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                        </svg>
                    </span>
                    <div className="tax-gpf-yearly-info">
                        <span className="tax-gpf-yearly-label">ภาษี หัก ณ ที่จ่าย รวมทั้งปี</span>
                        <span className="tax-gpf-yearly-value">{formatNumber(totalTax)} บาท</span>
                    </div>
                </div>
                {totalGpf > 0 && (
                    <div className="tax-gpf-yearly-box gpf">
                        <span className="tax-gpf-yearly-icon">
                            <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z"/>
                            </svg>
                        </span>
                        <div className="tax-gpf-yearly-info">
                            <span className="tax-gpf-yearly-label">กบข. รวมทั้งปี</span>
                            <span className="tax-gpf-yearly-value">{formatNumber(totalGpf)} บาท</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Monthly Chart */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">รายได้รายเดือน</div>
                <div className="steam-card-body">
                    <div className="monthly-chart">
                        {monthlyDetail.map((month, idx) => {
                            const maxValue = Math.max(...monthlyDetail.map(m => m.cnet || 0));
                            const percentage = maxValue > 0 ? ((month.cnet || 0) / maxValue) * 100 : 0;
                            return (
                                <div key={month.mm} className="chart-bar">
                                    <div className="bar-container">
                                        <div
                                            className="bar-fill"
                                            style={{ height: `${percentage}%` }}
                                        ></div>
                                    </div>
                                    <span className="bar-label">{thaiMonths[month.mm]}</span>
                                    <span className="bar-value">{formatNumber(month.cnet)}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Detail Tables */}
            <div className="detail-tables">
                {/* Income Table */}
                <div className="steam-card">
                    <div className="steam-card-header">
                        <span>สรุปรายการรับ</span>
                        <span className="badge badge-success">{incomeItems.length} รายการ</span>
                    </div>
                    <div className="steam-card-body" style={{ padding: 0 }}>
                        <table className="steam-table">
                            <thead>
                                <tr>
                                    <th>รหัส</th>
                                    <th>รายการ</th>
                                    <th className="text-right">รวมทั้งปี</th>
                                </tr>
                            </thead>
                            <tbody>
                                {incomeItems.map((item, idx) => (
                                    <tr key={idx}>
                                        <td>{item.code}</td>
                                        <td>{item.acc_name || `รหัส ${item.code}`}</td>
                                        <td className="text-right text-success">{formatNumber(item.total_amt)}</td>
                                    </tr>
                                ))}
                                <tr className="total-row">
                                    <td colSpan="2"><strong>รวมรายรับทั้งปี</strong></td>
                                    <td className="text-right"><strong>{formatNumber(summary.total_income)}</strong></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Deduction Table */}
                <div className="steam-card">
                    <div className="steam-card-header">
                        <span>สรุปรายการหัก</span>
                        <span className="badge badge-danger">{deductionItems.length} รายการ</span>
                    </div>
                    <div className="steam-card-body" style={{ padding: 0 }}>
                        <table className="steam-table">
                            <thead>
                                <tr>
                                    <th>รหัส</th>
                                    <th>รายการ</th>
                                    <th className="text-right">รวมทั้งปี</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deductionItems.map((item, idx) => (
                                    <tr key={idx}>
                                        <td>{item.code}</td>
                                        <td>{item.acc_name || `รหัส ${item.code}`}</td>
                                        <td className="text-right text-danger">{formatNumber(Math.abs(item.total_amt))}</td>
                                    </tr>
                                ))}
                                <tr className="total-row">
                                    <td colSpan="2"><strong>รวมรายหักทั้งปี</strong></td>
                                    <td className="text-right"><strong>{formatNumber(summary.total_deduction)}</strong></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default YearlySummary;
